import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { disconnectCalendar, CalendarConnection, updateMeetingAssistant } from '../../services/Calendar';
import { GoogleCalendarIcon, OutlookCalendarIcon } from '../icons/CalendarIcons';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { useI18n } from '../../i18n';

interface MeetingSettingsModalProps {
  open: boolean;
  onClose: () => void;
  calendars: CalendarConnection[];
  onDisconnectSuccess: () => void;
  meetingAssistantEnabled: boolean;
  onMeetingAssistantChange: (enabled: boolean) => void;
}

export const MeetingSettingsModal: React.FC<MeetingSettingsModalProps> = ({
  open,
  onClose,
  calendars,
  onDisconnectSuccess,
  meetingAssistantEnabled,
  onMeetingAssistantChange,
}) => {
  const { t } = useI18n();
  const m = t.meetings.settings;
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setShowConfirmation(false);
    setError(null);
    onClose();
  };

  const handleDisconnectClick = () => {
    setError(null);
    setShowConfirmation(true);
  };

  const handleConfirmDisconnect = async () => {
    // Get the first calendar (as per plan, we only show one disconnect button)
    if (calendars.length === 0) {
      setError(m.noCalendar);
      return;
    }

    const calendarId = calendars[0].id;
    setLoading(true);
    setError(null);

    const response = await disconnectCalendar(calendarId);

    if (response.success) {
      // Success! Close modal and trigger refresh
      setLoading(false);
      setShowConfirmation(false);
      onDisconnectSuccess();
    } else {
      // Show error
      setLoading(false);
      setShowConfirmation(false);
      setError(response.errors?.[0]?.title || m.disconnectFailed);
    }
  };

  const handleToggleMeetingAssistant = async (checked: boolean) => {
    setToggling(true);
    setError(null);

    const response = await updateMeetingAssistant(checked);

    if (response.success) {
      onMeetingAssistantChange(checked);
    } else {
      setError(response.errors?.[0]?.title || m.updateAssistantFailed);
    }

    setToggling(false);
  };

  const calendar = calendars.length > 0 ? calendars[0] : null;
  const isGoogle = calendar?.provider === 'google_calendar';
  const providerName = isGoogle ? 'Google Calendar' : 'Microsoft Outlook';

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md z-[1300]">
        <DialogHeader>
          <DialogTitle className="text-center">{m.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Meeting Assistant Toggle Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <label htmlFor="meeting-assistant" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {m.assistant}
                </label>
                <p className="text-sm text-muted-foreground">
                  {m.assistantDescription}
                </p>
              </div>
              <Switch
                id="meeting-assistant"
                checked={meetingAssistantEnabled}
                onCheckedChange={handleToggleMeetingAssistant}
                disabled={toggling}
              />
            </div>
          </div>

          <Separator />

          {/* Provider Header */}
          {calendar && (
            <div className="flex flex-col items-center mb-6">
              <div className="mb-4">
                {isGoogle ? <GoogleCalendarIcon className="h-14 w-14" /> : <OutlookCalendarIcon className="h-14 w-14" />}
              </div>
              <h3 className="text-lg font-semibold">{providerName}</h3>
            </div>
          )}

          <Button
            variant="destructive"
            className="w-full"
            onClick={showConfirmation ? handleConfirmDisconnect : handleDisconnectClick}
            disabled={loading || calendars.length === 0}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {m.disconnecting}
              </>
            ) : (
              showConfirmation ? m.confirmDisconnect : m.disconnect
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

